/** Deployment and production Release history query containers. Read-only by design. */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Button, FormControl, TextInput, UnderlineNav } from '@primer/react';

import {
  parseReleaseListState,
  releaseDetailFocusKey,
  releaseQueryPhase,
  releaseText,
  safeReleaseOperationView,
  safeReleasePageView,
  shouldFocusReleaseDetail,
} from '../../logic/releases.js';
import {
  ReleaseHistoryView,
  ReleaseOperationDetailView,
} from '../components/release-history-view.js';
import { useConsoleMutation, useDeployment, useDeployments, useRelease, useReleaseConfiguration, useReleasePlans, useReleases } from '../queries.js';
import { useStore } from '../store-context.js';

const PAGE_SIZE = 20;

export function ReleasesScreen(): ReactNode {
  const [searchParams] = useSearchParams();
  if ((searchParams.get('view') ?? 'drafts') === 'drafts') return <ReleaseWorkspace />;
  return <ReleaseHistoryWorkspace searchParams={searchParams} />;
}

function ReleaseHistoryWorkspace({ searchParams }: { readonly searchParams: URLSearchParams }): ReactNode {
  const listState = parseReleaseListState(new Map(searchParams.entries()));
  const page = { limit: PAGE_SIZE, offset: listState.offset };
  const releases = useReleases(page, listState.mode === 'releases');
  const deployments = useDeployments(page, listState.mode === 'deployments');
  const query = listState.mode === 'releases' ? releases : deployments;
  const pageView = query.data === undefined ? null : safeReleasePageView(query.data);
  const malformedError = query.data !== undefined && pageView === null
    ? new Error('The API returned malformed release history data.')
    : null;
  const shownError = query.error ?? malformedError;
  const items = pageView?.items ?? [];
  const phase = releaseQueryPhase({ isPending: query.isPending, error: shownError, itemCount: items.length });

  return (
    <ReleaseHistoryView
      listState={listState}
      phase={phase}
      error={shownError}
      operations={items}
      pagination={pageView?.pagination ?? null}
      onRetry={() => void query.refetch()}
    />
  );
}

function ReleaseWorkspace(): ReactNode {
  const plans = useReleasePlans();
  const configuration = useReleaseConfiguration();
  const [title, setTitle] = useState('');
  const [selection, setSelection] = useState<Record<string, { action: string; environment: string }>>({});
  const create = useConsoleMutation(
    (data, context, value: string) => data.createReleasePlan(value, context),
    [['release-plans']],
  );
  const remove = useConsoleMutation(
    (data, context, args: { releaseId: string; card: string }) => data.removeReleaseTask(args.releaseId, args.card, context),
    [['release-plans'], ['board']],
  );
  const deploy = useConsoleMutation(
    (data, context, args: { releaseId: string; action: string; environment: string }) => data.deployRelease(args.releaseId, args.action, args.environment, context),
    [['release-plans'], ['operator-chats']],
  );
  const cancel = useConsoleMutation(
    (data, context, releaseId: string) => data.settleReleasePlan(releaseId, 'CANCELLED', context),
    [['release-plans'], ['board']],
  );
  const cancelRequest = useConsoleMutation(
    (data, context, releaseId: string) => data.cancelReleaseRequest(releaseId, context),
    [['release-plans'], ['operator-chats']],
  );
  const removePlan = useConsoleMutation(
    (data, context, releaseId: string) => data.deleteReleasePlan(releaseId, context),
    [['release-plans']],
  );
  const drafts = plans.data?.filter((value) => value.release.status === 'DRAFT') ?? [];
  const history = plans.data?.filter((value) => value.release.status !== 'DRAFT') ?? [];
  const actions = configuration.data?.actions ?? [];
  const mutationError = create.error ?? remove.error ?? deploy.error ?? cancel.error ?? cancelRequest.error ?? removePlan.error;

  return (
    <div className="stack releases-screen release-workspace">
      <ReleaseTabs current="drafts" />
      {mutationError ? <p className="release-error" role="alert">{mutationError.message}</p> : null}
      <section className="release-compose" aria-labelledby="release-compose-heading">
        <div>
          <h2 id="release-compose-heading">Prepare a release</h2>
          <p>Collect ready tasks, then ask the configured release agent to deploy the complete revision.</p>
        </div>
        <form className="release-create" onSubmit={(event) => {
          event.preventDefault();
          if (title.trim().length === 0 || create.isPending) return;
          create.mutate(title.trim(), { onSuccess: () => setTitle('') });
        }}>
          <FormControl>
            <FormControl.Label visuallyHidden>Release name</FormControl.Label>
            <TextInput value={title} placeholder="Release name" onChange={(event) => setTitle(event.target.value)} />
          </FormControl>
          <Button variant="primary" type="submit" disabled={title.trim().length === 0 || create.isPending}>Create release</Button>
        </form>
      </section>
      {plans.isPending ? <p>Loading releases…</p> : plans.error ? <p role="alert">Could not load releases. {plans.error.message}</p> : drafts.length === 0 ? (
        <div className="release-empty-state"><h3>No draft releases</h3><p>Create one here or use “Add to release” on a ready task.</p></div>
      ) : (
        <div className="release-plan-grid">
          {drafts.map((detail) => {
            const current = selection[detail.release.id] ?? { action: actions[0]?.id ?? '', environment: actions[0]?.environments[0] ?? '' };
            const blocked = detail.tasks.filter((task) => !(task.deployableEnvironments?.includes(current.environment) ?? task.ready));
            const action = actions.find((candidate) => candidate.id === current.action);
            const environments = configuration.data?.environments.filter((environment) => action?.environments.includes(environment.id)) ?? [];
            const pending = detail.release.pendingMessageKey !== null;
            return (
              <article className="release-plan-card" key={detail.release.id}>
                <header><div><h3>{detail.release.title}</h3><p>Revision {detail.release.revision} · {detail.tasks.length} task{detail.tasks.length === 1 ? '' : 's'}</p></div>{pending ? <span className="release-pending">{detail.release.pendingRequestKind === 'CANCEL' ? 'Cancellation requested' : 'Request sent'}</span> : null}</header>
                {!pending && detail.release.resolutionSummary ? <div className="release-blocked" role="alert"><strong>Deployment not completed</strong><span>{detail.release.resolutionSummary}</span></div> : null}
                {blocked.length > 0 ? <div className="release-blocked" role="status"><strong>Deployment blocked</strong><span>{blocked.length} task{blocked.length === 1 ? ' is' : 's are'} no longer ready.</span></div> : null}
                <ul className="release-plan-tasks">
                  {detail.tasks.map((task) => <li key={task.taskBindingId}><div><strong>{task.title}</strong><span><code>{task.externalItemId}</code> · {task.stageId ?? 'Unknown stage'}{blocked.includes(task) ? ' · Blocked' : ''}</span></div><Button variant="invisible" onClick={() => remove.mutate({ releaseId: detail.release.id, card: task.externalItemId })}>Remove</Button></li>)}
                </ul>
                {detail.tasks.length === 0 ? <p className="release-plan-empty">Add tasks from cards in {configuration.data?.readyStage ?? 'the release-ready stage'}.</p> : null}
                <div className="release-deploy-controls">
                  <label>Action<select value={current.action} onChange={(event) => { const next = actions.find((candidate) => candidate.id === event.target.value); setSelection((value) => ({ ...value, [detail.release.id]: { action: event.target.value, environment: next?.environments[0] ?? '' } })); }}>{actions.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.label}</option>)}</select></label>
                  <label>Environment<select value={current.environment} onChange={(event) => setSelection((value) => ({ ...value, [detail.release.id]: { ...current, environment: event.target.value } }))}>{environments.map((environment) => <option key={environment.id} value={environment.id}>{environment.displayName}</option>)}</select></label>
                  <Button variant="primary" disabled={pending || blocked.length > 0 || detail.tasks.length === 0 || !current.action || !current.environment || deploy.isPending} onClick={() => {
                    if (action?.confirmation !== false && !globalThis.confirm(`Deploy ${detail.release.title} revision ${detail.release.revision} with ${detail.tasks.length} task(s) to ${environments.find((environment) => environment.id === current.environment)?.displayName ?? current.environment}?`)) return;
                    deploy.mutate({ releaseId: detail.release.id, ...current });
                  }}>{pending ? 'Waiting for agent…' : action?.label ?? 'Deploy'}</Button>
                  <Button variant="danger" disabled={detail.release.pendingRequestKind === 'CANCEL'} onClick={() => {
                    if (!globalThis.confirm(pending ? `Ask the release agent to cancel ${detail.release.title}?` : `Cancel ${detail.release.title}? Tasks will remain ready to deploy.`)) return;
                    pending ? cancelRequest.mutate(detail.release.id) : cancel.mutate(detail.release.id);
                  }}>{pending ? 'Cancel request' : 'Cancel release'}</Button>
                  {detail.tasks.length === 0 && detail.deployments.length === 0 && !pending ? <Button variant="danger" disabled={removePlan.isPending} onClick={() => { if (globalThis.confirm(`Delete empty release ${detail.release.title}?`)) removePlan.mutate(detail.release.id); }}>Delete</Button> : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {history.length === 0 ? null : <section><h2>Recently closed</h2><ul className="release-closed-list">{history.map((detail) => (
        <ClosedReleaseItem
          key={detail.release.id}
          title={detail.release.title}
          status={detail.release.status}
          summary={detail.release.resolutionSummary}
        />
      ))}</ul></section>}
    </div>
  );
}

function ClosedReleaseItem({ title, status, summary }: {
  readonly title: string;
  readonly status: string;
  readonly summary: string | null;
}): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const summaryRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    if (expanded) return;
    const paragraph = summaryRef.current;
    if (paragraph === null) return;
    const measure = (): void => setTruncated(paragraph.scrollHeight > paragraph.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(paragraph);
    return () => observer.disconnect();
  }, [expanded, summary]);

  return <li className={summary ? 'has-summary' : undefined}>
    <div className="release-closed-heading">
      <strong>{title}</strong>
      <span>{status}</span>
    </div>
    {summary ? <div className="release-closed-summary">
      <p ref={summaryRef} className={expanded ? 'is-expanded' : undefined}>{summary}</p>
      {!expanded && !truncated ? null : <Button
        size="small"
        variant="invisible"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >{expanded ? 'Show less' : 'Show more'}</Button>}
    </div> : null}
  </li>;
}

function ReleaseTabs({ current }: { readonly current: 'drafts' | 'history' }): ReactNode {
  return <UnderlineNav aria-label="Release views" variant="flush" className="release-tabs">
    <UnderlineNav.Item as={Link} to="/releases?view=drafts" {...(current === 'drafts' ? { 'aria-current': 'page' as const } : {})}>Drafts</UnderlineNav.Item>
    <UnderlineNav.Item as={Link} to="/releases?view=deployments" {...(current === 'history' ? { 'aria-current': 'page' as const } : {})}>History</UnderlineNav.Item>
  </UnderlineNav>;
}

export function DeploymentDetailScreen(): ReactNode {
  return <ReleaseOperationDetailScreen kind="deployment" />;
}

export function ReleaseDetailScreen(): ReactNode {
  return <ReleaseOperationDetailScreen kind="release" />;
}

function ReleaseOperationDetailScreen({ kind }: { readonly kind: 'deployment' | 'release' }): ReactNode {
  const { id = null } = useParams();
  const [searchParams] = useSearchParams();
  const returnParams = new Map(searchParams.entries());
  if (!returnParams.has('view')) returnParams.set('view', kind === 'deployment' ? 'deployments' : 'releases');
  const returnState = parseReleaseListState(returnParams);
  const store = useStore();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focusedRouteRef = useRef<string | null>(null);
  const deployment = useDeployment(kind === 'deployment' ? id : null);
  const release = useRelease(kind === 'release' ? id : null);
  const query = kind === 'deployment' ? deployment : release;
  const operation = query.data === undefined ? null : safeReleaseOperationView(query.data);
  const malformedError = query.data !== undefined && operation === null
    ? new Error(`The API returned malformed ${kind} detail data.`)
    : null;
  const shownError = query.error ?? malformedError;
  const phase = releaseQueryPhase({ isPending: query.isPending, error: shownError, itemCount: operation === null ? 0 : 1 });
  const routeFocusKey = releaseDetailFocusKey(kind, id);
  const focusTargetAvailable = phase === 'ready' || phase === 'error' || phase === 'empty';

  useEffect(() => {
    if (!shouldFocusReleaseDetail(focusedRouteRef.current, routeFocusKey, focusTargetAvailable)) return;
    // Parent route effects run after child effects. Defer one task so the
    // detail target wins over the shell's generic landmark focus. Commit the
    // key inside the task so StrictMode's setup/cleanup replay cannot cancel
    // the only scheduled focus while leaving the route marked as focused.
    const timer = globalThis.setTimeout(() => {
      if (!shouldFocusReleaseDetail(focusedRouteRef.current, routeFocusKey, true)) return;
      focusedRouteRef.current = routeFocusKey;
      store.announce(`Opened ${kind} ${releaseText(id, 'detail')}`);
      headingRef.current?.focus({ preventScroll: false });
    }, 0);
    return () => globalThis.clearTimeout(timer);
  }, [focusTargetAvailable, id, kind, routeFocusKey, store]);

  return (
    <ReleaseOperationDetailView
      kind={kind}
      id={id}
      returnState={returnState}
      phase={phase}
      error={shownError}
      operation={operation}
      headingRef={headingRef}
      onRetry={() => void query.refetch()}
    />
  );
}
